import { create } from "zustand";

/**
 * What the signed-in person is meant to work with.
 *
 * One CADViewer site serves several kinds of people — someone who only
 * consults plans, someone who annotates them, someone who maintains the
 * spaces, the administrator who set it up. Their role comes from the account
 * they signed in with; what that role sees comes from the profile the site
 * defines for it, fetched at sign-in.
 *
 * Absent means "nothing to say": a site that has not described this role, or
 * an API that could not be reached, leaves the viewer exactly as it was
 * published. Hiding an entry is about what someone works with, never a
 * substitute for the checks the endpoints behind them make.
 */
export type ToolboxKey =
  | "layerList"
  | "spaceCreation"
  | "spaceAssociation"
  | "vizQuerySetup"
  | "folderStructure"
  | "applicationSetup"
  | "aiSetup"
  | "spaceReporting"
  | "spaceAnalytics"
  | "textManipulation"
  | "dataImport";

interface RoleProfileState {
  /** The role of the signed-in user, lowercased, or null when signed out. */
  role: string | null;
  /** How the site names that role, for the interface. */
  label: string | null;
  /** Entries this role is shown. Empty means every entry stays visible. */
  toolbox: Partial<Record<ToolboxKey, boolean>>;
  /** True once an answer came back — used to avoid flashing a menu then hiding it. */
  resolved: boolean;
  setProfile: (profile: {
    role: string | null;
    label?: string | null;
    toolbox?: Partial<Record<ToolboxKey, boolean>>;
  }) => void;
  clear: () => void;
}

export const useRoleProfileStore = create<RoleProfileState>((set) => ({
  role: null,
  label: null,
  toolbox: {},
  resolved: false,
  setProfile: ({ role, label, toolbox }) =>
    set({ role, label: label ?? null, toolbox: toolbox ?? {}, resolved: true }),
  clear: () => set({ role: null, label: null, toolbox: {}, resolved: false }),
}));

/**
 * Whether one entry of the Space Tools menu is shown.
 *
 * A site with no profile for this role says nothing, and everything stays as
 * it was — which is what every deployment looked like before profiles existed.
 */
export function useCanSee(key: ToolboxKey): boolean {
  const toolbox = useRoleProfileStore((state) => state.toolbox);
  if (!toolbox || Object.keys(toolbox).length === 0) return true;
  return toolbox[key] !== false;
}
